import { apiClient } from './client';
import type {
  PaginatedDocumentsResponse,
  DocumentItem,
} from '@/types/document';

// Helper to extract filename from a Content-Disposition header
function extractFilename(
  contentDisposition?: string | null
): string | undefined {
  if (!contentDisposition) return undefined;
  // Typical format: attachment; filename="Case_Name__Applicant__2025-09-19.zip"
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
    contentDisposition
  );
  if (match) {
    return decodeURIComponent(match[1] || match[2]);
  }
  return undefined;
}

export class DocumentsApi {
  static async downloadArchive(
    caseId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const url = `/case/${caseId}/documents-archive/download`;
    const fullUrl = url.startsWith('http')
      ? url
      : `${(apiClient as any).baseURL}${url}`;
    const token = (apiClient as any).getAuthToken?.();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(fullUrl, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    const blob = await response.blob();
    const serverName = extractFilename(
      response.headers.get('Content-Disposition')
    );
    return { blob, filename: serverName || `documents-archive-${caseId}.zip` };
  }

  /**
   * Download a specific folder as a ZIP archive
   */
  static async downloadFolder(
    caseId: string,
    folderId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const url = `/case/${caseId}/documents-archive/folder/${folderId}/download`;
    const fullUrl = url.startsWith('http')
      ? url
      : `${(apiClient as any).baseURL}${url}`;
    const token = (apiClient as any).getAuthToken?.();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(fullUrl, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    const blob = await response.blob();
    const serverName = extractFilename(
      response.headers.get('Content-Disposition')
    );
    return { blob, filename: serverName || `folder-${folderId}.zip` };
  }

  /**
   * Download multiple folders as a ZIP archive
   */
  static async downloadMultipleFolders(
    caseId: string,
    folderIds: string[]
  ): Promise<{ blob: Blob; filename: string }> {
    const url = `/case/${caseId}/documents-archive/folders/download`;
    const fullUrl = url.startsWith('http')
      ? url
      : `${(apiClient as any).baseURL}${url}`;
    const token = (apiClient as any).getAuthToken?.();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ folderIds }),
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    const blob = await response.blob();
    const serverName = extractFilename(
      response.headers.get('Content-Disposition')
    );
    return { blob, filename: serverName || `folders-${caseId}.zip` };
  }

  static async listCaseDocuments(caseId: string): Promise<DocumentItem[]> {
    const res = await apiClient.get<any>(
      `/case/${encodeURIComponent(caseId)}/documents`
    );
    // Normalize shapes: { success, items }, or { data: { items } }, or plain { items }
    if (Array.isArray(res)) return res as any as DocumentItem[];
    if (res?.items && Array.isArray(res.items))
      return res.items as DocumentItem[];
    if (res?.data?.items && Array.isArray(res.data.items))
      return res.data.items as DocumentItem[];
    // Some backends might wrap in { success, meta, items }
    if (res?.success && res?.items && Array.isArray(res.items))
      return res.items as DocumentItem[];
    return [];
  }

  /**
   * Get document preview URL and metadata
   * Returns a preview URL that can be used to display the document
   */
  static async getDocumentPreview(
    documentId: string
  ): Promise<{
    success: boolean;
    previewUrl?: string;
    fileName?: string;
    mimeType?: string;
    error?: string;
  }> {
    try {
      const res = await apiClient.get<any>(
        `/documents/documents/${encodeURIComponent(documentId)}/preview`
      );
      return res;
    } catch (error: any) {
      console.error('Failed to get document preview:', error);
      return {
        success: false,
        error: error.message || 'Failed to load document preview',
      };
    }
  }

  /**
   * Get direct document serve URL with authentication
   * This returns a URL that serves the document directly through the backend
   */
  static getDocumentServeUrl(documentId: string): string {
    const baseURL = (apiClient as any).baseURL || '';
    return `${baseURL}/documents/documents/${encodeURIComponent(documentId)}/serve`;
  }
}

export default DocumentsApi;
